import dayjs from 'dayjs'
import { ReactNode, useMemo, useState } from 'react'

import { cn } from '@/app/utils'
import { getServerNow } from '@/components/entities/common/model/server-time'
import { toMskCalendarDate } from '@/utils/datetime'

import { AppCheckboxBadged } from '../../checkbox'
import {
  DEFAULT_PERMISSIONS_EXPIRES_AT_QUICK_FILTERS,
  PERMISSIONS_EXPIRES_AT_FORMAT,
  PERMISSIONS_EXPIRES_AT_LABEL,
  PERMISSIONS_EXPIRES_AT_UNLIMITED_LABEL,
  PermissionsExpiresAtQuickFilter
} from '../model/permissionsExpiresAt'
import { getDateLabel } from '../utils/getDateLabel'
import { CalendarType, DateRange } from './Calendar.types'
import { CalendarDropdown } from './CalendarDropdown'

type Props = {
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  className?: string
  invalid?: boolean
  invalidMessage?: ReactNode
  dataTestId?: string
  quickFilters?: PermissionsExpiresAtQuickFilter[]
}

/**
 * Выбор срока действия прав на ЛС («Действует до»).
 *
 * Отличия от общего календаря вынесены сюда, чтобы не менять поведение
 * заявок, пропусков и паркинга:
 * - начало промежутка всегда сегодняшний день по МСК (anchorFrom),
 *   наружу отдаётся только выбранная дата;
 * - выбрать можно строго со следующего дня, верхней границы нет: сегодняшний день
 *   остаётся подсвеченным в календаре, но клик по нему ничего не выбирает;
 * - повторный клик по любой дате переназначает конец промежутка без сброса;
 * - «Выбрать» неактивна, пока дата не выбрана;
 * - в режиме месяцев выбранной считается последний день месяца.
 *
 * «Сегодня» берётся от серверного времени, а не от часов браузера.
 */
export const PermissionsExpiresAtPicker = ({
  value,
  onChange,
  placeholder = PERMISSIONS_EXPIRES_AT_LABEL,
  className,
  invalid,
  invalidMessage,
  dataTestId,
  quickFilters = DEFAULT_PERMISSIONS_EXPIRES_AT_QUICK_FILTERS
}: Props) => {
  const [today, setToday] = useState(() => toMskCalendarDate(getServerNow()))

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setToday(toMskCalendarDate(getServerNow()))
    }
  }

  const dateRange: DateRange = useMemo(
    () => ({ from: today, to: value ? dayjs(value).toDate() : undefined }),
    [value, today]
  )

  const getQuickFilterValue = (filter: PermissionsExpiresAtQuickFilter) =>
    filter.months === undefined
      ? null
      : dayjs(today)
          .add(filter.months, 'month')
          .format(PERMISSIONS_EXPIRES_AT_FORMAT)

  const findQuickFilterLabel = (currentValue: string | null) =>
    quickFilters.find(filter => getQuickFilterValue(filter) === currentValue)
      ?.label ?? null

  const activeQuickFilterLabel = findQuickFilterLabel(value)

  const handleRangeChange = (range?: DateRange) => {
    onChange(
      range?.to ? dayjs(range.to).format(PERMISSIONS_EXPIRES_AT_FORMAT) : null
    )
  }

  const handleQuickFilterClick = (filter: PermissionsExpiresAtQuickFilter) => {
    onChange(getQuickFilterValue(filter))
  }

  const formatInputValue = (range: DateRange) =>
    range.to
      ? getDateLabel({ from: range.to, to: range.to })
      : PERMISSIONS_EXPIRES_AT_UNLIMITED_LABEL

  return (
    <div className={cn('flex flex-col', className)}>
      <CalendarDropdown
        type={CalendarType.Range}
        dateRange={dateRange}
        onRangeChange={handleRangeChange}
        placeholder={placeholder}
        className="bg-white"
        calendarClassName="w-[314px] xxs:w-full"
        showQuickFilters={false}
        onOpenChange={handleOpenChange}
        disabled={{ before: today }}
        anchorFrom={today}
        applyRequiresSelection
        selectMonthAsRangeEnd
        showOnlyRangeEnd
        formatInputValue={formatInputValue}
        invalid={invalid}
        invalidMessage={invalidMessage}
        dataTestId={dataTestId}
      />

      <div className="mt-8 flex flex-wrap gap-8">
        {quickFilters.map(filter => {
          const isActive = filter.label === activeQuickFilterLabel

          return (
            <AppCheckboxBadged
              key={filter.label}
              checked={isActive}
              onChange={() => {
                if (isActive) {
                  return
                }

                handleQuickFilterClick(filter)
              }}
              label={filter.label}
              closable={false}
            />
          )
        })}
      </div>
    </div>
  )
}
